<template>
  <div
    class="greyactivitybackground"
    :class="mobile() ? 'activity-survey-container-mobile' : 'activity-survey-container'"
  >
    <AlertMessages
      v-if="alertValue"
      :alert-type="alertType" 
      :alert-msg="alertMsg"
      @reset="alertValue = false"
    />

    <!-- EDIT MODE -->
    <v-card
      max-width="960px"
      width="100%"
      height="100%"
      class="edit-mode edit-mode-survey mx-auto"
      color="greyactivitybackground"
      elevation="0"
    >
      <v-form
        ref="form"
        v-model="valid"
        class="fill-height py-4"
        @submit.prevent
        @keydown.enter.prevent
      >
      <v-row
        v-if="published && !loadingData"
        no-gutters
        class="text-center mb-10 mt-6"
      >
        <v-col cols="12">
          <v-chip
            data-testid="editing-published-survey-chip"
            color="warning pa-6"
            text-color="black"
          >
            {{ $t('editPublishedSurveyTag') }}
          </v-chip>
        </v-col>
      </v-row>
      <div v-if="!loadingData">
        <div
          class="list-group-item first"
          :class="!surveys.length ? 'mb-0' : 'mb-4'"
        >
          <StartScreen data-testid="start-screen" ref="start" :anonymous="isAnonymous" :title="startTitle" :description="startDescription" :imageDisplay="startImageDisplay" :image="startImage" :color="startColor == null ? '#FFFFFF' : startColor" @resetEditor="editor = null" @setEditor="setEditor" @updateStartTitle="updateSurveyStartTitle" @updateStartDescription="updateSurveyStartDescription" @updateSurveyImage="updateSurveyImage" @showHideEditorTextOptions="showHideEditorTextOptions" @enableEditMode="showBlocks=false, showEditMode=true" @showBlockSettings="showBlockSettings('start')"/>
        </div>
        <draggable
          v-if="!surveys.length"
          ref="dragAreaSurvey"
          class="dragAreaSurvey list-group mx-auto py-2"
          :list="surveys"
          group="blocks"
          ghost-class="ghost-block"
          @change="log"
        >
          <v-row
            no-gutters
            align="center"
            data-testid="empty-page-content"
          >
            <v-col
              cols="12"
              class="mx-auto pa-0"
              align="center"
            >
              <div ref="editSurvey" />
              </v-col>
          </v-row>
        </draggable>
        
        <!-- :draggable property is added because was having problems in some browsers -->
        <!-- https://github.com/ueberdosis/tiptap/issues/379 -->
        <draggable
          v-else
          ref="dragAreaSurvey"
          class="list-group"
          :list="surveys"
          group="blocks"
          ghost-class="ghost-block"   
          :sort="false"
          :draggable="showBlocks"
          data-testid="surveys-list"
          @change="log"
        >
          <div
            v-for="(question, index) in surveys"
            :key="index"
            class="list-group-item mb-4"
        > 
            <v-icon
              v-if="index != 0 && !published"
              class="moveup-icon d-none"
              color="accent"
              data-testid="moveup-icon"
              dense
              @click="moveBlock(index, index-1)"
            >
              mdi-chevron-up
            </v-icon>
            <v-toolbar
              flat
              class="d-none"
              :height="published ? '32px' : '94px'"
            >
              <v-tooltip
                :bottom="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm || $vuetify.breakpoint.md"
                :right="$vuetify.breakpoint.lg || $vuetify.breakpoint.xl"
              >
                <template v-slot:activator="{ on }">
                  <v-icon
                    v-if="!published"
                    class="d-block pa-1"
                    data-testid="delete-icon"
                    dense
                    color="accent"
                    v-on="on"
                    @click="deleteBlock(index)"
                  >
                    mdi-delete
                  </v-icon>
                </template>
                <span>{{ $t('deleteBlock') }}</span>
              </v-tooltip>
              <v-tooltip
                :bottom="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm || $vuetify.breakpoint.md"
                :right="$vuetify.breakpoint.lg || $vuetify.breakpoint.xl"
              >
                <template v-slot:activator="{ on }">
                  <v-icon
                    v-if="!published"
                    class="d-block pa-1"
                    data-testid="copy-icon"
                    dense
                    color="accent"
                    v-on="on"
                    @click="copyBlock($event, question, index)"
                  >
                    mdi-content-copy
                  </v-icon>
                </template>
                <span>{{ $t('duplicateBlock') }}</span>
              </v-tooltip>
              <v-tooltip
                :bottom="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm || $vuetify.breakpoint.md"
                :right="$vuetify.breakpoint.lg || $vuetify.breakpoint.xl"
              >
                <template v-slot:activator="{ on }">
                  <v-icon
                    class="d-block pa-1"
                    data-testid="settings-icon"
                    dense
                    color="accent"
                    v-on="on"
                    @click="showBlockSettings(question, index)"
                  >
                    mdi-cog
                  </v-icon>
                </template>
                <span>{{ $t('settingsBlock') }}</span>
              </v-tooltip>
              <v-spacer />
            </v-toolbar>
            <div class="content-block pa-8 rounded-xxl" :style="`background-color:` + question.bg_color" @click="contentClickActions(question.qid)">
              <div>
                <v-row
                  align="center"
                  no-gutters
                  class="text-center"
                >
                  <v-col cols="12">
                    <editor-survey
                      ref="editTitle"
                      :key="updateEditorSurvey"
                      data-testid="title-editor"
                      :content="question.question"
                      :index="index"
                      part="title"
                      :placeholder="'surveyQuestionPlaceholder'+question.type"
                      @proceedClick="setEditor"
                      @updateSurvey="updateSurveyTitle"
                      @proceedMark="showHideEditorTextOptions"
                      @enableEditMode="showEditMode = true, showBlocks = false"
                      @showErrorMessage="showErrorMessageTitle"
                      class="text-h6"
                    />
                    <div
                      v-if="question.errorTitle && (strip(question.question).trim() == '' || question.question == null)"
                        class="error--text text-caption text-left"
                      >
                      {{ $t('fieldErrorRequired', { 'field': $t('labelTitle') }) }}
                    </div>
                  </v-col>
                  <v-col cols="12" class="mt-3">
                    <editor-survey
                      ref="editDescription"
                      :key="updateEditorSurvey"
                      data-testid="description-editor"
                      :content="question.description"
                      :index="index"
                      part="description"
                      @proceedClick="setEditor"
                      @updateSurvey="updateSurveyDescription"
                      @proceedMark="showHideEditorTextOptions"
                      @enableEditMode="showEditMode = true, showBlocks = false"
                      class="text-subtitle-2 font-weight-regular greytext--text"
                    />
                    </v-col>
                </v-row>
                <v-row v-if="question.type == 1" align="center" justify="center" data-testid="survey-1" class="mt-5 mb-3">
                  <v-col cols="7">
                    <MultipleChoiceTextQuestionTextAnswer :question="question" :published="published" @deleteAnswer="deleteAnswer" @resetEditor="editor = null"/>
                  </v-col>
                </v-row>
                <v-row v-else-if="question.type == 2" align="center" justify="center" data-testid="survey-2" class="mt-5 mb-3">
                  <v-col cols="12">
                    <MultipleChoiceTextQuestionTextImageAnswer :question="question" :index="index" :published="published" @deleteAnswer="deleteAnswer" @resetEditor="editor = null" @setEditor="setEditor" @updateSurveyImage="updateSurveyImage" @showHideEditorTextOptions="showHideEditorTextOptions" @enableEditMode="showBlocks=false, showEditMode=true" @showErrorMessageImage="showErrorMessageImage"/>
                  </v-col>
                </v-row>
                <v-row v-else-if="question.type == 3" align="center" justify="center" data-testid="survey-3" class="mt-5 mb-3">
                  <v-col cols="7">
                    <MultipleChoiceTextImageQuestionTextAnswer :question="question" :index="index" :published="published" @deleteAnswer="deleteAnswer" @resetEditor="editor = null" @setEditor="setEditor" @updateSurveyImage="updateSurveyImage" @showHideEditorTextOptions="showHideEditorTextOptions" @enableEditMode="showBlocks=false, showEditMode=true" @showErrorMessageImage="showErrorMessageImage"/>
                  </v-col>
                </v-row>
                <v-row v-else-if="question.type == 4" align="center" justify="center" data-testid="survey-4" class="my-5">
                  <v-col cols="7">
                    <OpenText :question="question"/>
                  </v-col>                 
                </v-row>
                <v-row v-else-if="question.type == 5" align="center" justify="center" data-testid="survey-5" class="my-5">
                  <v-col cols="7">
                    <OpenTextAttachment :question="question"/>
                  </v-col>                 
                </v-row>
                <v-row v-else-if="question.type == 6" align="center" justify="center" data-testid="survey-6" class="my-5">
                  <v-col cols="12">
                    <RatingStars :question="question" :key="updateRating"/>
                  </v-col>                 
                </v-row>
                <v-row v-else-if="question.type == 7" align="center" justify="center" data-testid="survey-7" class="my-5">
                  <v-col cols="12">
                    <RatingNumbers :question="question" :key="updateRating"/>
                  </v-col>                 
                </v-row>
                <v-row v-else-if="question.type == 8" align="center" justify="center" data-testid="survey-8" class="my-5">
                  <v-col cols="12">
                    <RatingNumbersComment :question="question" :key="updateRating"/>
                  </v-col>                 
                </v-row>
                <v-row v-if="question.type == 1 || question.type == 2 || question.type == 3" no-gutters align="center" justify="center">
                  <v-col :cols="question.type == 1 || question.type == 3 ? 7: 12">
                    <v-btn
                      v-if="!published"
                      data-testid="answer-button"
                      icon
                      :ripple="false"
                      class="no-hover text-none text-decoration-underline font-italic accent--text font-weight-regular"
                      @click="addAnswer(question)"
                      text
                      style="min-width:140px"
                    >
                      {{ $t('addSurveyChoice') }}                 
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
            </div>     
            <v-icon
              v-if="index != (surveys.length-1) && !published"
              class="movedown-icon d-none"
              color="accent"
              data-testid="movedown-icon"
              dense
              @click="moveBlock(index, index+1)"
            >
              mdi-chevron-down
            </v-icon> 
          </div>
        </draggable>
        <div
          class="list-group-item last"
        >
          <EndScreen data-testid="end-screen" ref="end" :anonymous="isAnonymous" :title="endTitle" :description="endDescription" :imageDisplay="endImageDisplay" :image="endImage" :color="endColor == null ? '#FFFFFF' : endColor" @resetEditor="editor = null" @setEditor="setEditor" @updateEndTitle="updateSurveyEndTitle" @updateEndDescription="updateSurveyEndDescription" @updateSurveyImage="updateSurveyImage" @showHideEditorTextOptions="showHideEditorTextOptions" @enableEditMode="showBlocks=false, showEditMode=true" @showBlockSettings="showBlockSettings('end')"/>
        </div>
      </div>
      </v-form>
    </v-card>   

    <v-navigation-drawer
      app
      permanent
      right
      clipped
      data-testid="navigation-drawer"
      width="300"
    >
      <template
        v-if="!loadingData"
        v-slot:prepend
      >
        <v-row class="ma-0">
          <v-col cols="5"> 
            <v-tooltip
              v-if="loadingAutosave && !published"
              top
            >
              <template
                v-slot:activator="{ on }"
              >
                <v-icon
                  data-testid="saving-draft-icon"
                  dense
                  class="mr-2"
                  v-on="on"
                  @click="autosaveSurveyAsDraft(false)"
                >
                  mdi-cloud-sync-outline
                </v-icon>
              </template>
              <span>{{ $t('savingTooltip') }}</span>
            </v-tooltip>
            <v-tooltip
              v-if="!loadingAutosave && !published"
              top
            >
              <template
                v-slot:activator="{ on }"
              >
                <v-icon
                  data-testid="save-draft-icon"
                  dense
                  class="mr-2"
                  v-on="on"
                  @click="autosaveSurveyAsDraft(false)"
                >
                  mdi-cloud-outline
                </v-icon>
              </template>
              <span>{{ $t('saveAsDraftTooltip') }}</span>
            </v-tooltip>
            <div
              class="text-caption grey--text font-weight-regular pt-1"
              style="height:20px; line-height:1;"
            >
              {{ loadingAutosaveFinish ? $t('saveAsDraftSuccess') : '' }}
            </div>
          </v-col>
          <v-col
            cols="7"
            align="right"
          >                    
            <v-btn
              v-if="!published && surveys.length > 0"
              data-testid="submit-button"
              class="rounded-xxl text-none px-10"
              color="success"
              dark 
              @click="
                dialogPublishVisible=true, 
                confirmationDialogTitle=$t('confirmationDialogTitlePublishSurvey'), 
                confirmationDialogText=$t('confirmationDialogTextPublishSurvey'), 
                confirmationDialogSubmitBtn=$t('publish')
                confirmationDialogCancelBtn=$t('saveAsDraft')"
              @submit.prevent
              @keydown.enter.stop.prevent
            >
              {{ $t('publish') }}
            </v-btn>
            <v-btn
              v-else-if="!published && surveys.length == 0"
              data-testid="submit-button"
              class="rounded-xxl text-none px-10"
              color="success"
              dark 
              @click="
                dialogNoQuestionVisible=true, 
                confirmationDialogTitle=$t('confirmationDialogTitleNoQuestionsInSurvey'), 
                confirmationDialogText=$t('confirmationDialogTextNoQuestionsInSurvey'), 
                confirmationDialogSubmitBtn=$t('saveAsDraft')
                confirmationDialogCancelBtn=$t('cancel')"
            >
              {{ $t('publish') }}
            </v-btn>
            <v-btn
              v-else
              data-testid="submit-button-publish-changes"
              class="rounded-xxl text-none px-10"
              color="success"
              dark 
              @click="
                dialogPublishChangesVisible=true, 
                confirmationDialogTitle=$t('confirmationDialogTitleUpdateSurvey'), 
                confirmationDialogText=$t('confirmationDialogTextUpdateSurvey'), 
                confirmationDialogSubmitBtn=$t('confirmBtn')
                confirmationDialogCancelBtn=$t('cancel')"
            >
              {{ $t('publish') }}
            </v-btn>
          </v-col>
        </v-row>        
        <v-row>
          <v-col :cols="!published ? '6' : '12'">                    
            <div
              class="d-flex flex-column"
              style="align-items:center"
            >
              <v-btn
                icon
                class="mr-0 text-none"
                :class="{'accent--text': showEditMode}"
                data-testid="edit-button"
                @click="showEditMode = true, showBlocks = false, showSettings = false"
              >
                <v-icon class="mx-auto">
                  mdi-auto-fix
                </v-icon>
              </v-btn>   
              <span
                :style="{cursor: 'pointer'}"
                :class="{'accent--text': showEditMode}"
                @click="showEditMode = true, showBlocks = false, showSettings = false"
              >{{ $t('editorEditModeTitle') }}</span>
            </div>
          </v-col>
          <v-col
            v-if="!published"
            cols="6"
            align="center"
          >
            <div
              class="d-flex flex-column"
              style="align-items:center"
            >
              <v-btn
                icon
                class="mr-0 text-none"
                :class="{'accent--text': showBlocks}"
                data-testid="blocks-button"
                @click="showBlocks = true, showEditMode = false, showSettings = false"
              >
                <v-icon class="mx-auto">
                  mdi-view-grid-plus-outline
                </v-icon>
              </v-btn>   
              <span
                :style="{cursor: 'pointer'}"
                :class="{'accent--text': showBlocks}"
                @click="showBlocks = true, showEditMode = false, showSettings = false"
              >{{ $t('editorBlocksModeTitle') }}</span>
            </div>
          </v-col>
        </v-row>
        <v-divider />
        <v-select
          v-if="showBlocks && !loadingData"
          v-model="selectedBlock.id"
          class="rounded-xxl select-items ma-5" 
          :items="listBlockTypes" 
          item-value="id" 
          item-text="name" 
          solo
          hide-details="true"
          append-icon="mdi-chevron-down"
          dense
          data-testid="blocks-type-select" 
          @change="changeBlockType"
        />
        <v-divider />
      </template>
      <v-tooltip
        v-if="showBlocks && !loadingData"
        left
        :disabled="!published"
      >
        <template v-slot:activator="{ on }">
          <v-card
            height="100%"
            elevation="0"
            class="px-2"
            data-testid="blocks-card"
            v-on="on"
          >
            <div>
              <draggable
                class="dragAreaSurvey pb-5"
                :list="filteredQuestions"
                :group="{ name: 'blocks', pull: 'clone', put: surveys }"
                :clone="dragDropBlockToPageLayout"
                :sort="false"
                chosen-class="chosen-block"            
                @start="onStart"
                @end="onEnd"
                :disabled="published"
              >
                <v-card
                  v-for="element in filteredQuestions"
                  :key="element.id"
                  class="page-block"
                  data-testid="block-card"
                  :ripple="false"
                  @click="addQuestionToPageLayout(element)"
                  elevation="4"
                >
                  <v-card-subtitle data-testid="block-title-card">
                    {{ $t(element.name) }}
                  </v-card-subtitle>
                  <v-img
                    data-testid="block-img-card"
                    :src="getImgUrl(element.image)"
                  />
                  <v-card-actions>
                    <v-btn
                      data-testid="block-button-card"
                      text
                      class="mx-auto text-none text-body-2 greytext--text rounded-xxl"
                    >
                      {{ $t('addBlockToSurveyLink') }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </draggable>
            </div>
          </v-card>
        </template>
        <span>{{ $t('editPublishedSurveyTag') }}</span>
      </v-tooltip>
      <editor-settings
        v-if="showEditMode && !loadingData"
        :editor="editor"
        :editor-extension="editorExtension"
        :editor-extension-properties="editorExtensionProperties"
        :editor-text-size="editorTextSize"
        :editor-activate-link-option="editorActivateLinkOption"
        :editor-link-properties="editorLinkProperties"
        :colors="contentColors"
        page="survey"
        @disableChangingEditor="disableChangingEditor"
        @proceedUploading="proceedUploadingInEditor"
        @changeSurveyImage="updateSurveyImageAfter"
        @changeRecentColors="changeRecentColors"
      />
      <editor-block-settings
        v-if="showSettings && !loadingData"
        :question="selectedQuestion"
        :published="published"
        :key="selectedQuestion.qid"
        type="survey"
        :colors="contentColors"
        @changeBlockColor = "setBlockColor"
        @changeQuestionAllowedAnswers = "setQuestionAllowedAnswers"
        @changeQuestionRatingScale = "setQuestionRatingScale"
        @changeRecentColors="changeRecentColors"
      />
      <ConfirmationDialogCropper 
          v-if="dialogVisibleCropper"
          ref="confirmationDialog"
          :visible="dialogVisibleCropper" 
          :image-to-crop="require('../../assets/quiz_images/example_image_answer.png')"
          data-testid="confirmation-dialog"
        /> 
    </v-navigation-drawer>

    <ConfirmationDialog
      v-if="dialogPublishVisible"
      ref="confirmationDialogPublish"
      :visible="dialogPublishVisible"
      :cd-title="confirmationDialogTitle"
      :cd-text="confirmationDialogText"
      :cd-submit-btn="confirmationDialogSubmitBtn"
      :cd-cancel-btn="confirmationDialogCancelBtn"
      cd-submit-btn-color="accent"
      data-testid="confirmation-dialog-publish"
      @input="clickOutside"
      @close="saveSurvey(false)"
      @proceedSubmit="saveSurvey(true)"
    />
    <ConfirmationDialog
      v-if="dialogPublishChangesVisible"
      ref="confirmationDialogPublishChanges"
      :visible="dialogPublishChangesVisible"
      :cd-title="confirmationDialogTitle"
      :cd-text="confirmationDialogText"
      :cd-submit-btn="confirmationDialogSubmitBtn"
      :cd-cancel-btn="confirmationDialogCancelBtn"
      cd-submit-btn-color="accent"
      data-testid="confirmation-dialog-publish-changes"
      @close="dialogPublishChangesVisible=false"
      @proceedSubmit="saveSurvey(true, true)"
    />
    <ConfirmationDialog
      v-if="dialogNoQuestionVisible"
      ref="noQuestionDialog"
      :visible="dialogNoQuestionVisible"
      :cd-title="confirmationDialogTitle"
      :cd-text="confirmationDialogText"
      :cd-submit-btn="confirmationDialogSubmitBtn"
      :cd-cancel-btn="confirmationDialogCancelBtn"
      cd-submit-btn-color="accent"
      data-testid="confirmation-dialog-no-question"
      @input="clickOutside"
      @close="dialogNoQuestionVisible=false;"
      @proceedSubmit="saveSurvey(false)"
    /> 
  </div>
</template>
<script>
import i18n from '../../plugins/i18n'
import AlertMessages  from '../../components/AlertMessages'
import { repeatedMethodsMixin } from '../../mixins/repeatedMethodsMixin'
import { getActivity, getActivitySurvey, addQuestionsInActivitySurvey, getQuestionTypesInActivitySurvey, getSurveyQuestions, getImageURLsInActivitySurvey, updateActivitySurvey, updateActivity } from '../../api/activityApi'
import EditorSurvey from '../../components/EditorSurvey';
import EditorSettings from '../../components/EditorSettings';
import EditorBlockSettings from '../../components/EditorBlockSettings';

import draggable from "vuedraggable";
import { ruleRequired, answerRuleValid } from '../../rules/formRules.js';
import { uuid } from 'vue-uuid'
import ConfirmationDialogCropper from '../../components/ConfirmationDialogCropper';
import Vue from "vue";
import MultipleChoiceTextQuestionTextAnswer from '../../components/editor_components/survey/MultipleChoiceTextQuestionTextAnswer'
import MultipleChoiceTextQuestionTextImageAnswer from '../../components/editor_components/survey/MultipleChoiceTextQuestionTextImageAnswer'
import MultipleChoiceTextImageQuestionTextAnswer from '../../components/editor_components/survey/MultipleChoiceTextImageQuestionTextAnswer'
import RatingNumbersComment from '../../components/editor_components/survey/RatingNumbersComment'
import OpenText from '../../components/editor_components/survey/OpenText'
import OpenTextAttachment from '../../components/editor_components/survey/OpenTextAttachment'
import RatingNumbers from '../../components/editor_components/survey/RatingNumbers'
import RatingStars from '../../components/editor_components/survey/RatingStars'
import StartScreen from '../../components/editor_components/survey/StartScreen'
import EndScreen from '../../components/editor_components/survey/EndScreen.vue';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import {eventBus} from '../../eventbus'

export default {
    components: {
    AlertMessages,
    EditorSettings,
    EditorSurvey,
    draggable,
    ConfirmationDialogCropper,
    EditorBlockSettings,
    MultipleChoiceTextQuestionTextAnswer,
    MultipleChoiceTextQuestionTextImageAnswer,
    MultipleChoiceTextImageQuestionTextAnswer,
    RatingNumbersComment,
    OpenText,
    OpenTextAttachment,
    RatingNumbers,
    RatingStars,
    StartScreen,
    EndScreen,
    ConfirmationDialog
},
    mixins: [repeatedMethodsMixin],
    data(){
         return{
            surveyId: this.$route.params.surveyId,
            activityId: this.$route.params.id,
            occupyInterval:null,
            alertMsg: '',
            alertType: '',
            alertValue:false,
            imageData: null,
            loadingImage: false,
            spaceId: window.$cookies.get('space_id'),
            coverURL: null,
            coverPath: null,
            loadingData: false,
            showBlocks: true,
            showEditMode: false,
            loadingAutosave: false,
            loadingAutosaveFinish: false,
            draftVersion: false,
            editorExtension: '',
            editorExtensionProperties: {},
            editorTextSize: 'p-normal',
            editorActivateLinkOption: false,
            editor: null,
            editorLinkProperties: {},
            updateEditorSurvey: null,
            updateRating: null,
            surveys: [],
            disableChangingEditorVal: false,
            published: false,
            preventRedirect: false,
            role:'',
            listBlockTypes:[
                {
                    id: 'choice',
                    name: i18n.t('multipleChoice')
                },
                {
                    id: 'text',
                    name: i18n.t('text')
                },
                {
                    id: 'rating',
                    name: i18n.t('ratingScale')
                }
            ],
            selectedBlock: {
                id: 'choice',
            },
            listQuestionTypes: [],
            blockType: 'choice',
            inputRules: ruleRequired('labelAnswer').concat(answerRuleValid),
            valid: true,
            dialogVisibleCropper: false,
            selectedType: '',
            selectedQuestionIndex: '',
            selectedAnswerIndex: '',
            selectedQuestion: {},
            showSettings: false,
            isAnonymous: false,
            startTitle: null,
            startDescription: null,
            endTitle: null,
            endDescription: null,
            startImage: null,
            startImageDisplay: null,
            endImage: null,
            endImageDisplay: null,
            startColor: null,
            endColor: null,
            surveyStartEndScreenChange: null,
            dialogPublishVisible: false,
            dialogPublishChangesVisible: false,
            dialogNoQuestionVisible: false,
            confirmationDialogTitle: '',
            confirmationDialogText: '',
            confirmationDialogCancelBtn: '',
            confirmationDialogSubmitBtn: '',
            contentColors: []
        }
    },
    computed:{
        filteredQuestions() {
            return this.listQuestionTypes.filter((item) =>{
                return item.group.toLowerCase().match(this.blockType)
            });
        },  
        productPlan() {
        return this.$store.state.productPlan;
      }, 
    },
    watch:{
        showBlocks(val){
            if(val){
                this.disableEditors()
            }
        },
        showEditMode(val){
            if(val){
                this.enableEditors()
            }
        },
        preventRedirect(val){
          eventBus.$emit('showDialogOnExit', val)
        }
    },
    mounted() { 
      if(this.productPlan.attributes && this.productPlan.attributes.name != 'Free'){
        this.occupyEditor(); 
        this.occupyInterval = setInterval(() => {  
          this.occupyEditor(); 
        }, 60000)
      }
    },
    async created(){
        //if someone else is editing
        if(this.$store.state.list.find(user => user.entity_id == this.surveyId )){
            this.$router.push({name: 'ViewActivitySurvey', params: {id: this.activityId, surveyId: this.surveyId, preventEditing: true}})
        }

        eventBus.$on('saveSurvey',()=>{
          if(this.published){
            this.saveSurvey(true, true)
          }else{
            this.saveSurvey(false)
          }
        })

        eventBus.$on('allowRedirect',()=>{
            this.preventRedirect = false
        })

        this.loadingData = true
        await this.getActivity()
         if (this.role == 'participant' || this.role == 'guest') {
          this.$router.push({name: 'PageAccessDenied'})
        }
        await this.getActivitySurvey()
        await this.getQuestionTypes()
        await this.getSurveyQuestions()

        this.loadingData = false   

        this.$watch('surveys', function () {
            if(!this.preventRedirect){
                this.preventRedirect = true
                if(!this.published){
                  this.autosaveInterval = setInterval(() => this.autosaveSurveyAsDraft(true), 15000);
                }
            }
        }, { deep: true })  

        this.$watch('surveyStartEndScreenChange', () => {
            if(!this.preventRedirect){
                this.preventRedirect = true
                if(!this.published){
                  this.autosaveInterval = setInterval(() => this.autosaveSurveyAsDraft(true), 15000);
                }
            }
        })

        if (this.published) {
          this.showEditMode = true
          this.showBlocks = false
          this.showSettings = false
        }
   },  

   beforeMount() {
        window.addEventListener("beforeunload", this.preventNav)
    },
    beforeDestroy() {
        window.removeEventListener("beforeunload", this.preventNav);
        eventBus.$off('saveSurvey')
        eventBus.$off('allowRedirect')
        clearInterval(this.autosaveInterval)
        clearInterval(this.occupyInterval)
    },

    methods:{
        occupyEditor(){
          var payload = {'key1': this.surveyId, 'key2': this.activityId,'key3': 'survey' }        
            this.$store.dispatch("occupyEditor", payload).catch(() => {
                    this.alertValue = true
                    this.alertType = 'error'
                    this.alertMsg =  i18n.t('serverError')                  
              })     
        },
        releaseEditor(){
          var payload = {'key1': this.surveyId, 'key2': this.activityId,'key3': 'survey' }
          this.$store.dispatch("releaseEditor", payload)
          .catch(() => {
              this.alertValue = true
              this.alertType = 'error'
              this.alertMsg =  i18n.t('serverError') 
            
            })
        },
         async getActivity(){
            try {
                const response = await getActivity(this.activityId)
                if(response.status == 200) {
                    this.role = response.data.data.attributes.role
                    this.contentColors = response.data.data.attributes.content_colors == null ? [] : response.data.data.attributes.content_colors
                }
            } catch (e) {
                if(e.response){
                    if(e.response.status == 404) {
                        if(this.isAuth){
                            this.$router.push({name: 'PageNotFound'})
                        }else{
                            this.$router.push({name: 'CustomLogin', query: {referer: this.$route.fullPath}})
                        }
                    }else if(e.response.status == 403){
                        this.$router.push({name: 'PageAccessDenied'})
                    }else {
                        this.alertValue = true
                        this.alertType = 'error'
                        this.alertMsg =  i18n.t('serverError')
                    }
                }
            }
         },
        async getActivitySurvey(){
          try {
                const response = await getActivitySurvey(this.surveyId)
                if(response.status == 200) {
                    this.isAnonymous = response.data.data.attributes.is_anonymous
                    this.published = response.data.data.attributes.published

                    if(response.data.data.attributes.start_title != null){
                        this.startTitle = response.data.data.attributes.start_title
                    }else {
                        this.startTitle = i18n.t('surveyStartTitleText')
                    }
                    if(response.data.data.attributes.start_description != null){
                        this.startDescription = response.data.data.attributes.start_description
                    }else {
                        this.startDescription =  i18n.t('surveyStartDescriptionText')
                    }

                    if(response.data.data.attributes.start_image != null){
                        this.startImage = response.data.data.attributes.start_image
                        this.startImageDisplay = response.data.data.attributes.start_image_url

                        if(response.data.data.attributes.start_image.indexOf('http') > -1){
                          this.startImageDisplay = this.startImage
                        }
                    }else {
                        this.startImage = null
                        this.startImageDisplay = null
                    }

                    if(response.data.data.attributes.start_bg_color != null){
                        this.startColor = response.data.data.attributes.start_bg_color
                    }

                    if(response.data.data.attributes.end_title != null){
                        this.endTitle = response.data.data.attributes.end_title
                    }else {
                        this.endTitle = i18n.t('surveyEndTitleText')
                    }
                    if(response.data.data.attributes.end_description != null){
                        this.endDescription = response.data.data.attributes.end_description
                    }else {
                        this.endDescription =  i18n.t('surveyEndDescriptionText')
                    }

                    if(response.data.data.attributes.end_image != null){
                        this.endImage = response.data.data.attributes.end_image
                        this.endImageDisplay = response.data.data.attributes.end_image_url

                        if(response.data.data.attributes.end_image.indexOf('http') > -1){
                          this.endImageDisplay = this.endImage
                        }
                    }else {
                        this.endImage = null
                        this.endImageDisplay = null
                    }

                    if(response.data.data.attributes.end_bg_color != null){
                        this.endColor = response.data.data.attributes.end_bg_color
                    }
                }  
            } catch (e) {
                if(e.response){
                    if(e.response.status == 404) {
                         this.$router.push({name: 'PageNotFound'})
                    }else {
                        this.alertValue = true
                        this.alertType = 'error';
                        this.alertMsg =  i18n.t('serverError');
                    }
                } 
            }

        },

        async getQuestionTypes(){
            try {
                const response = await getQuestionTypesInActivitySurvey()
                if(response){
                    if(response.status == 200) {                        
                        response.data.data.forEach(type => {
                            if(type.id == 1){
                                this.listQuestionTypes.push({ id: type.id, name: "surveyChoiceBlock1", image: "surveychoicetext.jpg", group: 'choice'})
                            }
                            else if(type.id == 2){
                                this.listQuestionTypes.push({ id: type.id, name: "surveyChoiceBlock2", image: "surveychoiceimage.jpg", group: 'choice'})
                            }
                            else if(type.id == 3){
                                this.listQuestionTypes.push({ id: type.id, name: "surveyChoiceBlock3", image: "surveychoicetextandimage.jpg", group: 'choice'})
                            }
                            else if(type.id == 4){
                                this.listQuestionTypes.push({ id: type.id, name: "surveyTextAnswerBlock1", image: "surveyopentext.jpg", group: 'text'})
                            }
                            else if(type.id == 5){
                                this.listQuestionTypes.push({ id: type.id, name: "surveyTextAnswerBlock2", image: "surveyopentextandattachment.jpg", group: 'text'})
                            }
                            else if(type.id == 6){
                                this.listQuestionTypes.push({ id: type.id, name: "surveyRatingBlock1", image: "surveystarrating.jpg", group: 'rating'})
                            }
                            else if(type.id == 7){
                                this.listQuestionTypes.push({ id: type.id, name: "surveyRatingBlock2", image: "surveynumberrating.jpg", group: 'rating'})
                            }
                            else if(type.id == 8){
                                this.listQuestionTypes.push({ id: type.id, name: "surveyRatingBlock3", image: "surveyratingcomment.jpg", group: 'rating'})
                            }
                        })
                    }                    
                }            
            } catch (e) {
                if(e.response){
                    this.alertValue = true
                    this.alertType = 'error'
                    this.alertMsg =  i18n.t('serverError')
                }            
            }
        },

        async getSurveyQuestions(){
            try {
                const response = await getSurveyQuestions(this.surveyId)
                if(response){
                    if(response.status == 200) { 
                      for (const value of response.data.data) {
                        if(value.attributes.type == 1 || value.attributes.type == 2 || value.attributes.type == 3){
                          var responseQuestionImageURL = await this.changeImageURLs(value.attributes.image)
                          var options= []
                          for (const answer of value.attributes.options) {
                              var responseAnswerImageURL = await this.changeImageURLs(answer.image)  
                              options.push(
                                  {
                                      aid: answer.aid,
                                      option: answer.option,
                                      image: answer.image,
                                      imageDisplay: responseAnswerImageURL,
                                  }
                              )
                          }
                          
                          this.surveys.push({
                              qid: value.id,
                              id: value.id, 
                              question: value.attributes.question,
                              description: value.attributes.description,
                              image: value.attributes.image,
                              imageDisplay: responseQuestionImageURL,
                              bg_color: value.attributes.bg_color,
                              allowed_options: value.attributes.allowed_options,
                              type: value.attributes.type,
                              options: options          
                          });

                        }else if(value.attributes.type == 4 || value.attributes.type == 5){
                          this.surveys.push({
                              qid: value.id,
                              id: value.id, 
                              question: value.attributes.question,
                              description: value.attributes.description,
                              image: null,
                              imageDisplay: null,
                              bg_color: value.attributes.bg_color,
                              type: value.attributes.type,
                          });

                        }else{
                          this.surveys.push({
                              qid: value.id,
                              id: value.id, 
                              question: value.attributes.question,
                              description: value.attributes.description,
                              image: null,
                              imageDisplay: null,
                              bg_color: value.attributes.bg_color,
                              allowed_options: value.attributes.allowed_options,
                              type: value.attributes.type,
                          });
                        }
                        
                      }
                    }                    
                }            
            } catch (e) {
                if(e.response){
                    this.alertValue = true
                    this.alertType = 'error'
                    this.alertMsg =  i18n.t('serverError')
                }            
            }
        },

        async changeImageURLs(value){
            var imagesToCheck = []
            if(value != null){
                imagesToCheck = [value]
                
                let dataImage = {
                    data: {
                        type: "surveys", 
                        id: this.surveyId,
                        attributes: {
                            paths: imagesToCheck,
                            space_id: this.spaceId
                        }
                    }
                };
                
                const responseImageQuestion = await getImageURLsInActivitySurvey(this.surveyId, dataImage)
                return responseImageQuestion.data.meta.files[value]
            }else {
                return null
            }
            
        },

        contentClickActions(item){
          if(this.showSettings){
            if(this.selectedQuestion.qid != item){
              this.showSettings = false
            }
          }
        },

        setEditor(editorSelected){
            this.showSettings = false

            if(editorSelected == null){
                this.editor = null
            }else {
                if(!this.disableChangingEditorVal){
                    this.editor = editorSelected
                    this.$nextTick(async () => {
                        if(editorSelected.view.state.selection.node){
                            this.editorExtension = editorSelected.view.state.selection.node.type.name
                            this.editorExtensionProperties = editorSelected.view.state.selection.node
                            if(this.editorExtensionProperties.attrs.path != null && this.editorExtensionProperties.attrs.path != this.editorExtensionProperties.attrs.src){
                                let data = {
                                    data: {
                                        type: "surveys", 
                                        id: this.surveyId,
                                        attributes: {
                                            paths: [this.editorExtensionProperties.attrs.path],
                                            space_id: this.spaceId
                                        }
                                    }
                                };
                                const response = await getImageURLsInActivitySurvey(this.surveyId, data)
                                this.editorExtensionProperties.attrs.src = response.data.meta.files[this.editorExtensionProperties.attrs.path]
                            }
                        }else{
                            this.editorExtension = 'text'
                            if(this.editor.isActive('custom-link')){
                                    this.editorLinkProperties = this.editor.getAttributes('custom-link')
                                }else if(this.editor.isActive('heading')){
                                    this.editorTextSize = 'h'+this.editor.getAttributes('heading').level
                                }else{
                                    if(this.editor.getAttributes('paragraph').class){
                                      if(this.editor.getAttributes('paragraph').class.includes('small')){
                                        this.editorTextSize = 'p-small'
                                      } else {
                                        this.editorTextSize = 'p-normal'
                                    }
                                  }                                
                              }
                        }
                    })
                }

            }
        },

        showErrorMessageTitle(index){
          Vue.set(this.surveys[index], "errorTitle", true);
        },

        showErrorMessageImage(index, answerIndex, type){
          if(type == 'answer'){
            Vue.set(this.surveys[index]['options'][answerIndex], "errorImage", true);

          }else{
            Vue.set(this.surveys[index], "errorImage", true);
          }
        },

        updateSurveyTitle(index, content){
            if(content != ''){
                this.surveys[index]['question'] = content
            }
        },

        updateSurveyStartTitle(content){
            if(content != ''){
                this.startTitle = content
                this.surveyStartEndScreenChange = Date.now()
            }
        },

        updateSurveyEndTitle(content){
            if(content != ''){
                this.endTitle = content
                this.surveyStartEndScreenChange = Date.now()
            }
        },

        updateSurveyDescription(index, content){
            if(content != ''){
                this.surveys[index]['description'] = content
            }
        },

        updateSurveyStartDescription(content){
          if(content != ''){
              this.startDescription = content
              this.surveyStartEndScreenChange = Date.now()
          }
        },

        updateSurveyEndDescription(content){
          if(content != ''){
              this.endDescription = content
              this.surveyStartEndScreenChange = Date.now()
          }
        },

        updateSurveyImage(type, index, answerIndex, content){
            if(content != ''){
              this.selectedType = type
              this.selectedQuestionIndex = index
              this.selectedAnswerIndex = answerIndex
            }
        },
        updateSurveyImageAfter(url, path){
            if(this.selectedType == 'answer'){
              this.surveys[this.selectedQuestionIndex]['options'][this.selectedAnswerIndex]['image'] = path
              this.surveys[this.selectedQuestionIndex]['options'][this.selectedAnswerIndex]['imageDisplay'] = url
            }else if(this.selectedType == 'question'){
              this.surveys[this.selectedQuestionIndex]['image'] = path
              this.surveys[this.selectedQuestionIndex]['imageDisplay'] = url
            }else if(this.selectedType == 'start'){
              this.startImage = path
              this.startImageDisplay = url
              this.surveyStartEndScreenChange = Date.now()
            }else{
              this.endImage = path
              this.endImageDisplay = url
              this.surveyStartEndScreenChange = Date.now()
            }
        },
        showHideEditorTextOptions(val){
            this.editorActivateLinkOption = val
        },
        disableEditors(){
            if(this.$refs.editSurvey && Array.isArray(this.$refs.editSurvey)){
                this.$refs.editSurvey.forEach((value, index) => {
                    if(this.$refs.editSurvey[index].editorSurvey.isEditable){
                        this.$refs.editSurvey[index].editorSurvey.setEditable(false)
                    }                  
                });
            }
        },
        enableEditors(){
            if(this.$refs.editSurvey && Array.isArray(this.$refs.editSurvey)){
                this.$refs.editSurvey.forEach((value, index) => {
                    if(!this.$refs.editSurvey[index].editorSurvey.isEditable){
                        this.$refs.editSurvey[index].editorSurvey.setEditable(true)
                    }                  
                });
            }              
        },

        getImgUrl(element) {
            var images = require.context('../../assets/editor_images', false, /\.jpg$/)
            return images('./' + element)           
        },

        changeBlockType(type){
            this.blockType = type
        },

        dragDropBlockToPageLayout(evt) {
          if (!this.published ) {
            let data = {}
            if(evt.id == 1){
              data = {
                qid: uuid.v4(),
                question:'', 
                description: '',
                image: null,
                imageDisplay: null,
                bg_color: '#FFFFFF',
                allowed_options: null,
                type: evt.id,
                options:[
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 0 },
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 1 },
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 2 },
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 3 }
                ]
              };
              return data
            }
            else if(evt.id == 2){
              data = {
                qid: uuid.v4(),
                question:'', 
                description: '',
                image: null,
                imageDisplay: null,
                bg_color: '#FFFFFF',
                allowed_options: null,
                type: evt.id,
                options:[
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 0 },
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 1 },
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 2 },
                ]
              };
              return data
            }else if(evt.id == 3){
              data = {
                qid: uuid.v4(),
                question:'', 
                description: '',
                image: null,
                imageDisplay: null,
                bg_color: '#FFFFFF',
                allowed_options: null,
                type: evt.id,
                options:[
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 0 },
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 1 },
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 2 },
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 3 }
                ]
              };
              return data
            }else if(evt.id == 4 || evt.id == 5){
              data = {
                qid: uuid.v4(),
                question:'', 
                description: '',
                image: null,
                imageDisplay: null,
                bg_color: '#FFFFFF',
                type: evt.id,
              };
              return data
            }else if(evt.id == 6){
              data = {
                qid: uuid.v4(),
                question:'', 
                description: '',
                image: null,
                imageDisplay: null,
                bg_color: '#FFFFFF',
                allowed_options: 5,
                type: evt.id,
              };
              return data
            }else if(evt.id == 7 || evt.id == 8){
              data = {
                qid: uuid.v4(),
                question:'', 
                description: '',
                image: null,
                imageDisplay: null,
                bg_color: '#FFFFFF',
                allowed_options: 10,
                type: evt.id,
              };
              return data
            }else{
              return data
            }
          }
        },
        onEnd(){
            // if(this.showBlocks){
            //     this.disableEditors()
            // }
            this.updateEditorSurvey = Date.now()
        },
        onStart(){
            this.$refs.dragAreaSurvey._sortable.options.draggable = ">*"
        },
        addQuestionToPageLayout(evt) {
          if (!this.published ) {
            let data = {}
            if(evt.id == 1){
              data = {
                qid: uuid.v4(),
                question:'', 
                description: '',
                image: null,
                imageDisplay: null,
                bg_color: '#FFFFFF',
                allowed_options: null,
                type: evt.id, 
                options:[
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 0 },
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 1 },
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 2 },
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 3 }
                ]
              };
            }
            else if(evt.id == 2){
              data = {
                qid: uuid.v4(),
                question:'', 
                description: '',
                image: null,
                imageDisplay: null,
                bg_color: '#FFFFFF',
                allowed_options: null,
                type: evt.id,
                options:[
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 0 },
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 1 },
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 2 },
                ]
              };
            }else if(evt.id == 3){
              data = {
                qid: uuid.v4(),
                question:'', 
                description: '',
                image: null,
                imageDisplay: null,
                bg_color: '#FFFFFF',
                allowed_options: null,
                type: evt.id,
                options:[
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 0 },
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 1 },
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 2 },
                  { aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: 3 }
                ]
              };
            }else if(evt.id == 4 || evt.id == 5){
              data = {
                qid: uuid.v4(),
                question:'', 
                description: '',
                image: null,
                imageDisplay: null,
                bg_color: '#FFFFFF',
                type: evt.id,
              };
            }else if(evt.id == 6){
              data = {
                qid: uuid.v4(),
                question:'', 
                description: '',
                image: null,
                imageDisplay: null,
                bg_color: '#FFFFFF',
                allowed_options: 5,
                type: evt.id,
              };
            }else if(evt.id == 7 || evt.id == 8){
              data = {
                qid: uuid.v4(),
                question:'', 
                description: '',
                image: null,
                imageDisplay: null,
                bg_color: '#FFFFFF',
                allowed_options: 10,
                type: evt.id,
              };
            }
            this.surveys.push(data);
            //this.$nextTick(() => {
                //this.$refs.last.$el.scrollIntoView()
            //})
            // if(this.showBlocks){
            //     this.disableEditors()
            // }
            this.updateEditorSurvey = Date.now()
          }
        },
        log: function() {
            //window.console.log(evt)
        },
        moveBlock(from, to) {
            this.surveys.splice(to, 0, this.surveys.splice(from, 1)[0])
            // if(this.showBlocks){
            //     this.disableEditors()
            // }
            this.updateEditorSurvey = Date.now()
        },
        deleteBlock(element){
            this.showSettings = false
            this.surveys.splice(element, 1);
            // if(this.showBlocks){
            //     this.disableEditors()
            // }
            this.updateEditorSurvey = Date.now()
            this.editorExtension = ''
            this.editorExtensionProperties = {}
        },
        copyBlock(e, element, index){
          this.showSettings = false
          if(this.surveys[index].type == 1 || this.surveys[index].type == 2 || this.surveys[index].type == 3){
            let options = JSON.parse(JSON.stringify(this.surveys[index].options))
            for(let i = 0; i < options.length; i++){
              options[i].aid = uuid.v4()
            }
            this.$nextTick(() => {
              this.surveys.splice(index+1, 0, {qid: uuid.v4(), question: this.surveys[index].question,  description: this.surveys[index].description, image: this.surveys[index].image, imageDisplay: this.surveys[index].imageDisplay, bg_color: this.surveys[index].bg_color, allowed_options: this.surveys[index].allowed_options, type: this.surveys[index].type, options:options });
            })
          }else if(this.surveys[index].type == 4 || this.surveys[index].type == 5){
            this.$nextTick(() => {
              this.surveys.splice(index+1, 0, {qid: uuid.v4(), question: this.surveys[index].question,  description: this.surveys[index].description, image: this.surveys[index].image, imageDisplay: this.surveys[index].imageDisplay, bg_color: this.surveys[index].bg_color, type: this.surveys[index].type});
            })
          }else if(this.surveys[index].type == 6 || this.surveys[index].type == 7 || this.surveys[index].type == 8){
            this.$nextTick(() => {
              this.surveys.splice(index+1, 0, {qid: uuid.v4(), question: this.surveys[index].question,  description: this.surveys[index].description, image: this.surveys[index].image, imageDisplay: this.surveys[index].imageDisplay, bg_color: this.surveys[index].bg_color, allowed_options: this.surveys[index].allowed_options, type: this.surveys[index].type});
            })
          }
          this.updateEditorSurvey = Date.now()
        },
        proceedUploadingInEditor(alertMsg){
            this.alertValue = true
            this.alertType = 'error';
            this.alertMsg = alertMsg
        },
        disableChangingEditor(val){
            this.disableChangingEditorVal = val
        },

        addAnswer(question){
            this.editor = null
            var questionItem = this.surveys.find(item => item.qid === question.qid)
            questionItem.options.push({ aid: uuid.v4(), option: "", "image": null, "imageDisplay": null, id: questionItem.options.length+1 })
        },

        deleteAnswer(question, index){
            var questionItem = this.surveys.find(item => item.qid === question.qid)
            questionItem.options.splice(index, 1)
        },

        async saveSurvey(publish = false, changesOnPublished = false){
          if(publish){
            this.valid = this.$refs.form.validate()

            if(this.strip(this.startTitle).trim() == '' || this.strip(this.endTitle).trim() == ''){
              this.valid = false
            }
           
            this.surveys.forEach((questionItem, index) => {
              if(questionItem.question == null || this.strip(questionItem.question).trim() == ''){
                Vue.set(this.surveys[index], "errorTitle", true);
                this.valid = false
              }
              if(questionItem.type == 3 && questionItem.imageDisplay == null){
                Vue.set(this.surveys[index], "errorImage", true);
                this.valid = false;
              }
              if(this.surveys[index]['options']){
                this.surveys[index]['options'].forEach((answerItem, indexa) => {
                  if(questionItem.type == 2 && answerItem.imageDisplay == null){
                    Vue.set(this.surveys[index]['options'][indexa], "errorImage", true);
                    this.valid = false
                  }
                })
              }
              
            })
            if(this.valid){
              this.surveys.forEach(questionItem => {
                delete questionItem.qid
                questionItem.question = questionItem.question == null? questionItem.question : questionItem.question.replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, '')
                questionItem.description = questionItem.description == null ? questionItem.description : questionItem.description.replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, '')
              })

              let data = {
                  data: {
                      type: "surveys",
                      attributes: {
                          questions: this.surveys,
                          published: true
                      },
                      relationships: {
                          activity: {
                              data: {
                                  type: "activities",
                                  id: this.$route.params.id
                              }
                          }
                      }
                  }
              };
              try {
                const response = await addQuestionsInActivitySurvey(this.surveyId, data)

                const space = window.location.host.split('.')[0];
                let dataSurvey = {
                  data: {
                      type: "surveys",
                      id: this.surveyId,
                      attributes: {
                          published: true,
                          start_title: this.startTitle == null ? this.startTitle : this.startTitle.replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, ''),
                          start_description: this.startDescription == null ? this.startDescription : this.startDescription.replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, ''),
                          start_image: this.startImage == null ? process.env.VUE_APP_PROTOCOL + "://" + space + "." + process.env.VUE_APP_DOMAIN + "/assets/editor_images/artboard1.png" : this.startImage,
                          start_bg_color: this.startColor,
                          end_title: this.endTitle == null ? this.endTitle : this.endTitle.replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, ''),
                          end_description: this.endDescription == null ? this.endDescription : this.endDescription.replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, ''),
                          end_image: this.endImage == null ? process.env.VUE_APP_PROTOCOL + "://" + space + "." + process.env.VUE_APP_DOMAIN + "/assets/editor_images/artboard2.png" : this.endImage,
                          end_bg_color: this.endColor
                      },
                      relationships: {
                          activity: {
                              data: {
                                  type: "activities",
                                  id: this.$route.params.id
                              }
                          }
                      }
                  }
                };

                const responseSurvey = await updateActivitySurvey(this.surveyId, dataSurvey)
                if (response.status == 200 && responseSurvey.status == 200) {
                  if(this.productPlan.attributes && this.productPlan.attributes.name != 'Free'){
                    this.releaseEditor()
                  }
                  this.preventRedirect = false
                  if (changesOnPublished) {
                      this.$router.push({name: 'ViewActivitySurvey', params: {id: this.$route.params.id, surveyId: this.surveyId, successfullyUpdatedPublishedActivitySurvey: true}})
                  } else {
                      this.$router.push({name: 'ViewActivitySurvey', params: {id: this.$route.params.id, surveyId: this.surveyId, successfullyPublishedActivitySurvey: true}})
                  }
                  
                }
              } catch (e) {
                if(e.response){
                  this.dialogPublishVisible = false
                  if (e.response.status == 403){
                    for(let i = 0; i < e.response.data.errors.length; i++){
                      if(e.response.data.errors[i].detail == 'This action is unauthorized.'){
                        this.$router.push({name: 'PageAccessDenied'})
                      }
                      else {
                        this.alertValue = true
                        this.alertType = 'error'
                        this.alertMsg =  i18n.t('serverError')
                      }
                    }
                  } else if (e.response.status == 422){
                    this.alertValue = true
                    this.alertType = 'error'
                    this.alertMsg =  i18n.t('questionValidationError')
                  } else{
                    this.alertValue = true
                    this.alertType = 'error'
                    this.alertMsg =  i18n.t('serverError') 
                  }           
                }
              }
            }else {
              this.dialogPublishVisible = false
              this.dialogNoQuestionVisible = false
              this.dialogPublishChangesVisible = false
              this.alertValue = true
              this.alertType = 'error'
              this.alertMsg =  i18n.t('questionValidationError')
            }
          }else{
            this.surveys.forEach(questionItem => {
              delete questionItem.qid
              questionItem.question = questionItem.question == null? questionItem.question : questionItem.question.replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, '')
              questionItem.description = questionItem.description == null ? questionItem.description : questionItem.description.replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, '')
            })

            let data = {
                data: {
                    type: "surveys",
                    attributes: {
                        questions: this.surveys,
                        published: false
                    },
                    relationships: {
                        activity: {
                            data: {
                                type: "activities",
                                id: this.$route.params.id
                            }
                        }
                    }
                }
            };
            try {
              const response = await addQuestionsInActivitySurvey(this.surveyId, data)

              const space = window.location.host.split('.')[0];
              let dataSurvey = {
                data: {
                  type: "surveys",
                  id: this.surveyId,
                  attributes: {
                    published: false,
                    start_title: this.startTitle == null ? this.startTitle : this.startTitle.replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, ''),
                    start_description: this.startDescription == null ? this.startDescription : this.startDescription.replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, ''),
                    start_image: this.startImage == null ? process.env.VUE_APP_PROTOCOL + "://" + space + "." + process.env.VUE_APP_DOMAIN + "/assets/editor_images/artboard1.png" : this.startImage,
                    start_bg_color: this.startColor,
                    end_title: this.endTitle == null ? this.endTitle : this.endTitle.replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, ''),
                    end_description: this.endDescription == null ? this.endDescription : this.endDescription.replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, ''),
                    end_image: this.endImage == null ? process.env.VUE_APP_PROTOCOL + "://" + space + "." + process.env.VUE_APP_DOMAIN + "/assets/editor_images/artboard2.png" : this.endImage,
                    end_bg_color: this.endColor
                  },
                  relationships: {
                    activity: {
                      data: {
                        type: "activities",
                        id: this.$route.params.id
                      }
                    }
                  }
                }
              };

              const responseSurvey = await updateActivitySurvey(this.surveyId, dataSurvey)
              if (response.status == 200 && responseSurvey.status == 200) {
                if(this.productPlan.attributes && this.productPlan.attributes.name != 'Free'){
                  this.releaseEditor()
                }
                this.preventRedirect = false
                this.$router.push({name: 'ViewActivitySurvey', params: {id: this.$route.params.id, surveyId: this.surveyId, successfullyUpdatedUnpublishedActivitySurvey: true}})
              } 
            } catch (e) {
              if(e.response){
                this.dialogNoQuestionVisible = false
                if (e.response.status == 403){
                  for(let i = 0; i < e.response.data.errors.length; i++){
                    if(e.response.data.errors[i].detail == 'This action is unauthorized.'){
                      this.$router.push({name: 'PageAccessDenied'})
                    }
                    else {
                      this.alertValue = true
                      this.alertType = 'error'
                      this.alertMsg =  i18n.t('serverError')
                    }
                  }
                } else if (e.response.status == 422){
                  this.alertValue = true
                  this.alertType = 'error'
                  this.alertMsg =  i18n.t('questionValidationError')
                } else{
                  this.alertValue = true
                  this.alertType = 'error'
                  this.alertMsg =  i18n.t('serverError') 
                }           
              }
            }
          }
        },

        async autosaveSurveyAsDraft(autosave){
            this.loadingAutosave = true
            this.loadingAutosaveFinish = false

            this.surveys.forEach(questionItem => {
                questionItem.question = questionItem.question == null? questionItem.question : questionItem.question.replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, '')
                questionItem.description = questionItem.description == null ? questionItem.description : questionItem.description.replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, '')
            })

            let data = {
                data: {
                    type: "surveys",
                    attributes: {
                        questions: this.surveys,
                        published: false
                    },
                    relationships: {
                        activity: {
                            data: {
                                type: "activities",
                                id: this.$route.params.id
                            }
                        }
                    }
                }
            };
            await addQuestionsInActivitySurvey(this.surveyId, data)

            const space = window.location.host.split('.')[0];
            let dataSurvey = {
            data: {
                type: "surveys",
                id: this.surveyId,
                attributes: {
                    published: false,
                    start_title: this.startTitle == null ? this.startTitle : this.startTitle.replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, ''),
                    start_description: this.startDescription == null ? this.startDescription : this.startDescription.replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, ''),
                    start_image: this.startImage == null ? process.env.VUE_APP_PROTOCOL + "://" + space + "." + process.env.VUE_APP_DOMAIN + "/assets/editor_images/artboard1.png" : this.startImage,
                    start_bg_color: this.startColor,
                    end_title: this.endTitle == null ? this.endTitle : this.endTitle.replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, ''),
                    end_description: this.endDescription == null ? this.endDescription : this.endDescription.replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, ''),
                    end_image: this.endImage == null ? process.env.VUE_APP_PROTOCOL + "://" + space + "." + process.env.VUE_APP_DOMAIN + "/assets/editor_images/artboard2.png" : this.endImage,
                    end_bg_color: this.endColor
                },
                relationships: {
                    activity: {
                        data: {
                            type: "activities",
                            id: this.$route.params.id
                        }
                    }
                }
            }
        };

            await updateActivitySurvey(this.surveyId, dataSurvey)
            if(autosave){
                this.loadingAutosave = false
            }else{
                this.loadingAutosave = false
                this.loadingAutosaveFinish = true
                setTimeout(() => {this.loadingAutosaveFinish = false}, 2000);
            }
        },

        showBlockSettings(question, index){
          this.editor = null
          this.editorExtension = ''
          this.editorExtensionProperties = {}
          this.showBlocks = false
          this.showEditMode = true
          this.showSettings = true
          if(question == 'start'){
            this.selectedQuestion = {qid: 'start', type: 'start', bg_color: this.startColor}
            this.selectedQuestionIndex = 'start'
          }else if(question == 'end'){
            this.selectedQuestion = {qid: 'end', type: 'end', bg_color: this.endColor}
            this.selectedQuestionIndex = 'end'
          }else{
            this.selectedQuestion = question
            this.selectedQuestionIndex = index
          }
        },

        setBlockColor(value){
          if(this.selectedQuestionIndex == 'start'){
            this.startColor = value
            this.surveyStartEndScreenChange = Date.now()
          }else if(this.selectedQuestionIndex == 'end'){
            this.endColor = value
            this.surveyStartEndScreenChange = Date.now()
          }else{
            this.surveys[this.selectedQuestionIndex]['bg_color'] = value
          }
          
        },
        setQuestionAllowedAnswers(value){
          this.surveys[this.selectedQuestionIndex]['allowed_options'] = value
        },
        setQuestionRatingScale(value){
          this.surveys[this.selectedQuestionIndex]['allowed_options'] = value
          this.updateRating = Date.now()
        },
        clickOutside(){
          this.dialogPublishVisible=false;
          this.dialogNoQuestionVisible=false
        },
        preventNav(event) {
            if(this.preventRedirect){
                event.preventDefault()
                event.returnValue = ""
            }
        },
        beforeRouteLeave (to, from , next) {
            if(this.preventRedirect){
                const answer = window.confirm('Do you really want to leave? Changes you made may not be saved.')
                if (answer) {
                  if(this.productPlan.attributes && this.productPlan.attributes.name != 'Free'){
                    this.releaseEditor();
                  }
                  next()
                } else {
                  next(false)
                }
            }else {
                next()
            }
        },
        async changeRecentColors(val){
          let data = {
            data: {
              type: "activities",
              id: this.activityId,
              attributes: {
                content_colors: val
              },
              relationships: {
                space: {
                  data: {
                    type: "spaces",
                    id: this.spaceId
                  }
                },
              }
            }
          }
          const response = await updateActivity(this.activityId, data)
          if (response.status == 200) {
            this.contentColors = val
          }
        },
    }
}
        
</script>